import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employees, EMPLOYMENT_TYPE_LABELS, type EmployeeFull, type EmploymentType } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

// Фільтр «чинний»: усі / лише чинні / лише не чинні.
type ActiveFilter = 'all' | 'active' | 'inactive';

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [q, setQ] = useState('');
  const [orgId, setOrgId] = useState('');  // '' = усі організації
  const [empType, setEmpType] = useState('');  // '' = усі види договору
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  // orgId → назва (колонка «Організація»).
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgList.data?.items ?? []) m.set(o.id, o.name);
    return m;
  }, [orgList.data]);

  const list = useQuery({
    queryKey: ['employees-full', activeFilter, q, orgId, empType],
    queryFn: () => employees.list({
      organization_id: orgId || undefined,
      active_only: activeFilter === 'active',
      employment_type: empType || undefined,
      q: q || undefined, limit: 500,
    }),
  });

  const rows = useMemo(() => {
    const items = list.data?.items ?? [];
    return activeFilter === 'inactive' ? items.filter((e) => !e.is_active) : items;
  }, [list.data, activeFilter]);

  const cols = useMemo<ColumnDef<EmployeeFull>[]>(() => [
    {
      key: 'is_active', header: 'Чинний', width: 70, align: 'center',
      sortValue: (e) => (e.is_active ? 1 : 0),
      cell: (e) => e.is_active
        ? <span title="Чинний" style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
        : <span title="Не чинний" style={{ color: '#94a3b8' }}>—</span>,
    },
    {
      key: 'code', header: 'Таб.№', width: 120, importBadge: true,
      sortValue: (e) => e.code ?? '', cell: (e) => e.code ?? '',
    },
    {
      // Без width — гнучка колонка: займає вільний простір, при нестачі обрізається.
      key: 'full_name', header: 'ПІБ',
      sortValue: (e) => (e.full_name ?? '').toLowerCase(), cell: (e) => e.full_name ?? '—',
    },
    {
      key: 'inn', header: 'ІПН', width: 130,
      sortValue: (e) => e.inn ?? '', cell: (e) => e.inn ?? '—',
    },
    {
      key: 'employment_type', header: 'Вид договору', width: 170,
      sortValue: (e) => EMPLOYMENT_TYPE_LABELS[e.employment_type] ?? e.employment_type ?? '',
      cell: (e) => EMPLOYMENT_TYPE_LABELS[e.employment_type] ?? e.employment_type ?? '—',
    },
    {
      key: 'org', header: 'Організація', width: 220,
      sortValue: (e) => orgName.get(e.organization_id) ?? '',
      cell: (e) => orgName.get(e.organization_id) ?? '—',
    },
  ], [orgName]);

  const selStyle = { width: 140, flex: '0 0 auto' } as const;

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Співробітники</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={selStyle}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <select value={empType} onChange={(e) => setEmpType(e.target.value)} style={selStyle}>
          <option value="">Усі види договору</option>
          {/* «Без договору» (NO_CONTRACT) прибрано з фільтра (запит власника
              2026-06-15). У колонці таблиці лейбл лишається для legacy-записів. */}
          {(Object.entries(EMPLOYMENT_TYPE_LABELS) as Array<[EmploymentType, string]>)
            .filter(([v]) => v !== 'NO_CONTRACT')
            .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
                style={selStyle}>
          <option value="all">Усі</option>
          <option value="active">Лише чинні</option>
          <option value="inactive">Лише не чинні</option>
        </select>
        <input placeholder="ПІБ або ІПН…" value={q}
               onChange={(e) => setQ(e.target.value)}
               style={{ width: 160, flex: '0 0 auto' }} />
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => navigate('/employees/new-unified')}>
          + Новий
        </button>
      </div>

      <DataTable<EmployeeFull>
        rows={rows}
        columns={cols}
        rowKey={(e) => e.id}
        loading={list.isLoading}
        emptyText="Немає співробітників."
        onRowDoubleClick={(e) => navigate(`/employees/${e.id}`)}
        rowMuted={(e) => e.deletion_mark ? 0.4 : (e.is_active ? undefined : 0.6)}
        initialSortKey="full_name"
      />
    </div>
  );
}
